import axios from "axios";
import { toast } from "react-toastify";
// Função utilitária para obter ID da empresa do localStorage
import { getEmpresaField } from '../utils/getEmpresaField';
import { formatPriceWithSymbol } from '../utils/currency';


// Detectar ambiente de desenvolvimento (localhost, 127.0.0.1, ou forçado via env)
const hostname = window.location.hostname;
const isDevelopment =
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.includes("ngrok") ||
  process.env.REACT_APP_FORCE_DEV === "true";

const apiUrl = (isDevelopment
  ? process.env.REACT_APP_SERVER_URL_DEV
  : process.env.REACT_APP_SERVER_URL_PROD
)?.replace(/\/+$/, ''); // Remover trailing slash para evitar URL com //

console.log(`[API] Ambiente: ${isDevelopment ? 'DESENVOLVIMENTO' : 'PRODUÇÃO'}, URL: ${apiUrl}`);

export const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
});

// Instância pública (sem Authorization por padrão)
export const apiPublic = axios.create({
  baseURL: apiUrl,
});

const getEmpresaId = () => getEmpresaField('id_empresa');

// ============================
// AUTH TOKEN MANAGEMENT (in-memory)
// ============================
let getAccessTokenFn = () => null;
let setAccessTokenFn = () => {};
let logoutFn = () => {};

// Guard para o logout por bloqueio: evita disparar logout/toast múltiplas vezes
// quando várias requests paralelas recebem 403 COMPANY_BLOCKED ao mesmo tempo.
let blockLogoutTriggered = false;

/**
 * Chamado pelo AuthContext para conectar o interceptor ao state do React.
 * Evita dependência circular entre api.js e AuthContext.
 */
export const configureAuth = ({ getAccessToken, setAccessToken, logout }) => {
  getAccessTokenFn = getAccessToken;
  setAccessTokenFn = setAccessToken;
  logoutFn = logout;
};

/**
 * Permite obter o access token atual fora do React (ex: Socket.IO hooks)
 */
export const getCurrentAccessToken = () => getAccessTokenFn();

// ============================
// SINGLE-FLIGHT REFRESH (in-tab + cross-tab)
// ============================
// Uma única Promise compartilhada por todos os callers da aba (visibility handler,
// proactive interval, response interceptor, sockets). Sem isso, wake-from-suspend
// dispara N refresh paralelos -> race condition na rotação no backend.
let inflightRefresh = null;

// Coordenação cross-tab via BroadcastChannel: se outra aba acabou de renovar,
// reutiliza o token dela em vez de bater no backend de novo.
const REFRESH_CHANNEL_NAME = 'pedeja-auth-refresh';
const refreshChannel = (typeof BroadcastChannel !== 'undefined')
  ? new BroadcastChannel(REFRESH_CHANNEL_NAME)
  : null;

// Pequena janela onde aceitamos um token "fresco" anunciado por outra aba
// sem precisar pedir refresh ao backend.
const CROSS_TAB_FRESH_WINDOW_MS = 8_000;
let lastBroadcastedToken = null;
let lastBroadcastedAt = 0;

if (refreshChannel) {
  refreshChannel.onmessage = (event) => {
    if (event?.data?.type === 'token-refreshed' && event.data.token) {
      lastBroadcastedToken = event.data.token;
      lastBroadcastedAt = event.data.at || Date.now();
      // Atualiza o token desta aba imediatamente
      try { setAccessTokenFn(event.data.token); } catch (_) { }
    }
  };
}

/**
 * Refresh the access token proactively (e.g., after PC wake from suspend).
 * Uses the HttpOnly refresh cookie — same mechanism as the response interceptor.
 *
 * Single-flight: chamadas concorrentes na MESMA aba compartilham a mesma Promise.
 * Cross-tab: chamadas em abas diferentes reutilizam o último token anunciado
 * via BroadcastChannel se ele for recente (<8s).
 *
 * @returns {string|null} The new access token, or null if refresh failed.
 */
export const refreshAccessTokenManually = async () => {
  // 1) Já temos um token muito recente vindo de outra aba? Reutiliza.
  if (
    lastBroadcastedToken &&
    Date.now() - lastBroadcastedAt < CROSS_TAB_FRESH_WINDOW_MS
  ) {
    setAccessTokenFn(lastBroadcastedToken);
    return lastBroadcastedToken;
  }

  // 2) Já tem refresh em vôo nesta aba? Aguarda o mesmo.
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/v1/auth/refresh`,
        {},
        { withCredentials: true }
      );
      if (data?.token) {
        setAccessTokenFn(data.token);

        // Anunciar o novo token para as outras abas
        lastBroadcastedToken = data.token;
        lastBroadcastedAt = Date.now();
        try {
          refreshChannel?.postMessage({
            type: 'token-refreshed',
            token: data.token,
            at: lastBroadcastedAt,
          });
        } catch (_) { }

        return data.token;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Libera o mutex no próximo tick para que callers que acabaram
      // de chegar peguem o token recém-setado em vez de iniciar outro refresh.
      setTimeout(() => { inflightRefresh = null; }, 0);
    }
  })();

  return inflightRefresh;
};

// ============================
// REQUEST INTERCEPTOR
// ============================
api.interceptors.request.use((config) => {
  try {
    // Usar token da memória (via AuthContext)
    const token = getAccessTokenFn();
    if (token) {
      if (!config.headers) config.headers = {};
      config.headers["Authorization"] = `Bearer ${token}`;
    } else if (config.headers) {
      delete config.headers["Authorization"];
    }

    // Não enviar Authorization para domínios externos
    if (config?.url && /^https?:\/\//.test(config.url)) {
      const requestUrl = new URL(config.url);
      const baseUrl = new URL(apiUrl);
      if (requestUrl.host !== baseUrl.host && config.headers) {
        delete config.headers["Authorization"];
      }
    }
  } catch (_) { }
  return config;
});

// ============================
// RESPONSE INTERCEPTOR
// Usa o single-flight de refreshAccessTokenManually para coordenar refresh
// entre TODAS as origens (interceptor, visibility handler, proactive interval,
// outras abas via BroadcastChannel). Resolve a race condition que matava a
// família de refresh tokens no backend após wake-from-suspend.
// ============================
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Só tentar refresh se for 401 com TOKEN_EXPIRED e não for a própria rota de refresh
    if (
      error.response?.status === 401 &&
      error.response?.data?.code === 'TOKEN_EXPIRED' &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;

      // Retry com backoff: rede instável pós-suspend pode falhar transientemente
      let newToken = null;
      for (let attempt = 1; attempt <= 2; attempt++) {
        newToken = await refreshAccessTokenManually();
        if (newToken) break;
        if (attempt < 2) {
          console.warn(`[API] Refresh tentativa ${attempt} falhou, retentando em 2s...`);
          await new Promise(r => setTimeout(r, 2000));
        }
      }

      if (newToken) {
        if (!originalRequest.headers) originalRequest.headers = {};
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      // Refresh falhou nas duas tentativas → sessão realmente expirada
      console.warn('[API] Refresh falhou - fazendo logout');
      logoutFn();
      return Promise.reject(error);
    }

    // 🔒 Conta bloqueada/inativa → logout automático imediato.
    // O backend retorna 403 COMPANY_BLOCKED/USER_BLOCKED no authMiddleware a cada
    // request, e 401 COMPANY_BLOCKED no /refresh. Em qualquer um, encerramos a sessão
    // na hora (admin inativou a empresa, ou o usuário foi bloqueado durante a sessão).
    const blockCode = error.response?.data?.code;
    if (
      (error.response?.status === 403 || error.response?.status === 401) &&
      (blockCode === 'COMPANY_BLOCKED' || blockCode === 'USER_BLOCKED')
    ) {
      // Idempotente: só a 1ª request bloqueada dispara logout/toast. As demais
      // (ex: polling da fila de atendimento + checagem de licença em paralelo)
      // são suprimidas para NÃO encher a tela de toasts de "erro ao carregar".
      if (!blockLogoutTriggered) {
        blockLogoutTriggered = true;
        console.warn(`[API] Conta bloqueada/inativa (${blockCode}) - logout automático`);
        try {
          toast.dismiss(); // limpa toasts de erro que componentes já tenham aberto
          toast.error(
            blockCode === 'USER_BLOCKED'
              ? 'Seu acesso foi bloqueado. Faça login novamente.'
              : 'Esta empresa foi desativada. Faça login novamente.',
            { autoClose: 6000 }
          );
        } catch (_) { /* toast pode não estar disponível */ }
        logoutFn();
        // Libera o guard após o redirect (permite novo ciclo se logar de novo)
        setTimeout(() => { blockLogoutTriggered = false; }, 5000);
      }
      // Promise que não resolve: a sessão está encerrando e a tela vai redirecionar
      // pro login, então as chamadas pendentes não devem acionar os .catch dos
      // componentes (que exibiriam toasts genéricos de erro).
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);











// Aqui abaixo são as rotas públicas, pode mantê-las no index.js do backend!

// ======================================================
// ROTAS PÚBLICAS
// ======================================================

export const createSession = async (email, password) => {
  return api.post("/api/v1/auth/login", { email, password });
};

export const getEmpresaWithObjId = async (empresaObjId) => {
  return api.get("/empresaWithObjId/" + empresaObjId);
};

export const getAdicionaisCardapio = async (id_empresa, nomeEmpresa) => {
  // Rota antiga /get-adicionais-cardapio/ foi deprecada - usar V2
  return api.get("/api/v1/catalogo/adicionais-private");
};

export const getCategoriasCardapio = async (id_empresa, nomeEmpresa) => {
  return api.post("/get-categorias-cardapio/", { id_empresa, nomeEmpresa });
};

export const getEmpresaInfo = async (id_empresa, nomeEmpresa) => {
  return api.post("/get-empresa-info", { id_empresa, nomeEmpresa });
};

export const getItensCardapio = async (id_empresa, nomeEmpresa) => {
  return api.post("/get-itens-cardapio/", { id_empresa, nomeEmpresa });
};

export const getItensCardapioSalao = async (id_empresa, nomeEmpresa) => {
  return api.post("/get-itens-cardapio-salao/", { id_empresa, nomeEmpresa });
};

export const getItemCardapio = async (id_empresa, itemObjId) => {
  return api.post("/get-item-cardapio/", { id_empresa, itemObjId });
};

export const updateClienteAddress = async (
  id_empresa,
  telefone,
  endereco,
  enderecoToEdit
) => {
  return api.post("/update-endereco-cliente/", {
    id_empresa,
    telefone,
    endereco,
    enderecoToEdit,
  });
};

export const deleteEnderecoCliente = async (
  id_empresa,
  telefone,
  enderecoIdToDelete
) => {
  return api.post("/delete-endereco-cliente/", {
    id_empresa,
    telefone,
    enderecoIdToDelete,
  });
};

export const registerPedido = async (
  createdBy,
  id_empresa,
  itens,
  celular_cliente,
  nome_cliente,
  tipo_pagamento,
  entrega,
  desconto,
  cpf_cnpj,
  valor_troco,
  valor_total,
  descricao,
  external_id,
  session_token = null,
  idempotency_key = null, // ✅ Chave de idempotência opcional
  // Parcelas do pagamento dividido: [{ tipo: 'cash'|'card'|'pix', valor, troco }].
  // Sem isto, um pagamento dividido chegava ao backend so como a string
  // "Dividido" e o caixa cobrava o valor inteiro como dinheiro na gaveta.
  pagamentos_divididos = null
) => {
  return api.post("/auth/registerPedido", {
    createdBy,
    id_empresa,
    itens,
    celular_cliente,
    nome_cliente,
    tipo_pagamento,
    entrega,
    desconto,
    cpf_cnpj,
    valor_troco,
    valor_total,
    descricao,
    external_id,
    session_token,
    idempotency_key,
    pagamentos_divididos
  });
};

export const calcularDistancia = async (coordenadasA, coordenadasB, id_empresa) => {
  // Rota antiga /calcular-distancia foi deprecada - usar /api/public/distance
  const { lat: latA, lng: lngA } = coordenadasA;
  const { lat: latB, lng: lngB } = coordenadasB;
  let url = `/api/public/distance?latA=${latA}&lngA=${lngA}&latB=${latB}&lngB=${lngB}`;
  if (id_empresa) {
    url += `&id_empresa=${id_empresa}`;
  }

  return apiPublic.get(url);
};

// ✅ VERSÃO SAFE QUE NUNCA REJEITA - PARA EVITAR UNCAUGHT COMPLETAMENTE
// 🔧 FIX: Usa rota pública /api/public/distance (a antiga /calcular-distancia foi depreciada)
export const calcularDistanciaSafe = async (coordenadasA, coordenadasB, id_empresa) => {
  try {
    const { lat: latA, lng: lngA } = coordenadasA;
    const { lat: latB, lng: lngB } = coordenadasB;
    let url = `/api/public/distance?latA=${latA}&lngA=${lngA}&latB=${latB}&lngB=${lngB}`;
    if (id_empresa) {
      url += `&id_empresa=${id_empresa}`;
    }
    const response = await apiPublic.get(url);
    return {
      success: true,
      data: response.data,
      status: response.status
    };
  } catch (error) {
    process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔍 [API-SAFE] Erro capturado em calcularDistanciaSafe:", error.message);
    return {
      success: false,
      error: error,
      status: error.response?.status || 500,
      data: error.response?.data || null
    };
  }
};

export const registerClienteFromCardapio = async (
  id_empresa,
  nome,
  telefone
) => {
  return api.post("/register-cliente-cardapio", { id_empresa, nome, telefone });
};

export const createEmpresaUser = async (
  cnpj,
  nome_empresa,
  email,
  telefone,
  type,
  nome_pessoa,
  password,
  confirmpassword,
  vinculo_empresa
) => {
  return api.post("/createEmpresaUser", {
    cnpj,
    nome_empresa,
    email,
    telefone,
    type,
    nome_pessoa,
    password,
    confirmpassword,
    vinculo_empresa,
    browserLanguage: navigator.language || 'pt-BR',
  });
};

export const updatePreferredLanguage = async (language) => {
  return api.put("/api/v1/empresa/preferred-language", { language });
};

export const getEstados = async () => {
  return apiPublic.get(
    "https://servicodados.ibge.gov.br/api/v1/localidades/estados/"
  );
};

export const getMunicipios = async (estadoSelecionado) => {
  return apiPublic.get(
    "https://servicodados.ibge.gov.br/api/v1/localidades/estados/" +
    estadoSelecionado +
    "/municipios"
  );
};














// ======================================================
// ROTAS USERS
// ======================================================

export const getUser = async (userID) => {
  return api.get("/api/v1/users/" + userID);
  //return api.get("/user/" + userID);
};

// GET /api/v1/users/list-by-role/:role (listar usuários por cargo e empresa)
export const getUsersByRole = async (id_empresa, role) => {
  return api.get(`/api/v1/users/list-by-role/${role}?id_empresa=${id_empresa}`);
};

// POST /api/v1/users/delete-user/:id_empresa (deletar usuário)
export const deleteUser = async (id, id_empresa) => {
  return api.post("/api/v1/users/delete-user/" + id_empresa, { id });
};














// ======================================================
// ROTAS ENTREGADORES
// ======================================================

// GET /api/v1/entregadores/ (listar entregadores)
// ✅ MIGRADA PARA entregadoresV2 - Usando req.context para segurança
export const getEntregadores = async () => {
  return api.get("/api/v1/entregadores");
};

















// ======================================================
// ROTAS CAIXA
// ======================================================

// ✅ MIGRADA PARA caixaV2 - Usando req.context para segurança
export const listarCaixas = async (id, page, startDate, endDate) => {
  // V2: Não envia mais ID na URL - usa dados do token automaticamente
  return api.get("/api/v1/caixa/list-caixas", {
    params: {
      page,
      per_page: 10,
      startDate,
      endDate,
    },
  });
};

// ✅ MIGRADA PARA caixaV2 - Usando req.context para segurança  
export const getCaixaById = async (userID, id_caixa) => {
  // V2: Não envia mais userID na URL - usa dados do token automaticamente
  return api.post("/api/v1/caixa/get-caixa", { id_caixa });
};

// ✅ MIGRADA PARA caixaV2 - Usando req.context para segurança
export const fecharCaixa = async (
  id_empresa,
  objIdCaixa,
  closedBy,
  saldo_final,
  valor_informado_dinheiro,
  valor_informado_cartao,
  valor_informado_pix
) => {
  // V2: Enviar valores individuais para cálculo correto dos valores esperados
  return api.post("/api/v1/caixa/close", {
    objIdCaixa,
    valor_informado_dinheiro,
    valor_informado_cartao,
    valor_informado_pix,
    observacoes: `Fechamento - Dinheiro: ${formatPriceWithSymbol(valor_informado_dinheiro)}, Cartão: ${formatPriceWithSymbol(valor_informado_cartao)}, PIX: ${formatPriceWithSymbol(valor_informado_pix)}`
  });
};

// ✅ MIGRADA PARA caixaV2 - Usando req.context para segurança
export const getLastCaixaSaldoFinal = async () => {
  // V2: Rota renomeada e não envia mais id_empresa (usa token)
  return api.get("/api/v1/caixa/last-saldo-final");
};

// ✅ MIGRADA PARA caixaV2 - Usando req.context para segurança
export const addLancamentoCaixa = async (
  id_empresa,
  objIdCaixa,
  descricao,
  tipo_lancamento,
  valor,
  createdBy
) => {
  // V2: Rota renomeada e campos ajustados
  return api.post("/api/v1/caixa/add-lancamento", {
    objIdCaixa,
    tipo: tipo_lancamento === 'Entrada' ? 'entrada' : 'saida',
    valor,
    descricao,
    categoria: tipo_lancamento // Mantém compatibilidade
  });
};

// ✅ MIGRADA PARA caixaV2 - Usando req.context para segurança
export const registerCaixaAndOpen = async (
  createdBy,
  id_empresa,
  saldo_inicial
) => {
  // V2: Rota renomeada e não envia mais id_empresa/createdBy (usa token)
  return api.post("/api/v1/caixa/register-and-open", {
    saldo_inicial
  });
};

// ✅ ENDPOINT TEMPORÁRIO - Sincronizar flags da empresa com estado real dos caixas
export const syncEmpresaFlags = async () => {
  return api.post("/api/v1/caixa/sync-empresa-flags");
};












// ======================================================
// ROTAS PEDIDOS
// ======================================================

export const getPedidos = async (userID, vinculo_empresa) => {
  // V2: Não envia mais IDs - usa dados do token automaticamente
  return api.post("/api/v1/pedidos/list", {});
};

// Buscar pedidos por telefone do cliente (para histórico no WhatsApp)
export const getPedidosByPhone = async (telefone, limit = 10) => {
  return api.get("/api/v1/pedidos", {
    params: { search: telefone, limit }
  });
};

export const getPedido = async (
  userID,
  id_empresa,
  vinculo_empresa,
  id_pedido
) => {
  try {
    // V2: Não envia mais id_empresa - usa dados do token automaticamente
    return await api.post("/api/v1/pedidos/by-id", {
      id_pedido,
    });
  } catch (error) {
    process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Erro ao buscar pedido", error);
    throw error;
  }
};

export const getPedidosByStatus = async (
  userID,
  id_empresa,
  vinculo_empresa,
  status_pedido,
  forReport
) => {
  // V2: Não envia mais IDs - usa dados do token automaticamente
  return api.post("/api/v1/pedidos/status", {
    status_pedido,
    forReport
  });
};

export const getPedidosByStatusSimples = async (
  userID,
  id_empresa,
  vinculo_empresa,
  status_pedido,
  forReport
) => {
  // V2: Não envia mais IDs - usa dados do token automaticamente
  return api.post("/api/v1/pedidos/status", {
    status_pedido,
    forReport
  });
};

export const getPedidosByStatusFinalizados = async (
  userID,
  id_empresa,
  vinculo_empresa,
  forReport
) => {
  // V2: Não envia mais IDs - usa dados do token automaticamente
  return api.post("/api/v1/pedidos/status", {
    status_pedido: '4',
    forReport: forReport === 'true' ? true : false
  });
};

export const getPedidosFinalizadosHistorico = async (
  userID,
  id_empresa,
  vinculo_empresa,
  status_pedido,
  periodo
) => {
  // V2: Não envia mais id_empresa - usa dados do token automaticamente
  return api.post("/api/v1/pedidos/historico", {
    status_pedido,
    periodo,
  });
};

export const getPedidosFinalizadosPeriodo = async (
  userID,
  id_empresa,
  vinculo_empresa,
  status_pedido,
  startDate,
  endDate
) => {
  // V2: Não envia mais id_empresa - usa dados do token automaticamente
  // Garantir que as datas são válidas
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  
  return api.post("/api/v1/pedidos/periodo", {
    status_pedido,
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  });
};











// ======================================================
// ROTAS CLIENTES
// ======================================================

// ✅ MIGRADA PARA clientesV2 - Usando req.context para segurança
export const getCliente = async (id_empresa, telefone) => {
  return api.post("/api/v1/clientes/by-phone", { id_empresa, telefone });
};

// ✅ MIGRADA PARA clientesV2 - Usando req.context para segurança  
export const getClienteByName = async (id_empresa, nome) => {
  return api.post("/api/v1/clientes/by-name", { id_empresa, nome });
};

export const getClientes = async (userID) => {
  // V2: Não envia mais IDs - usa dados do token automaticamente
  return api.get("/api/v1/clientes");
};


export const updateCliente = async (
  id,
  userID,
  documento,
  name,
  razao,
  contato,
  email,
  cep,
  estado,
  municipio,
  bairro,
  complemento,
  telefone,
  celular,
  type
) => {
  const payload = {
    cpf_cnpj: documento,
    nome: name,
    razao,
    contato,
    email,
    cep,
    estado,
    municipio,
    bairro,
    complemento,
    telefone,
    celular,
    type,
  };
  return api.put(`/api/v1/clientes/${id}`, payload);
};

export const deleteCliente = async (id, userID) => {
  return api.delete(`/api/v1/clientes/${id}`);
};


export const registerCliente = async (
  createdBy,
  documento,
  name,
  contato,
  razao,
  email,
  cep,
  estado,
  municipio,
  bairro,
  complemento,
  telefone,
  celular,
  vinculo_empresa,
  type
) => {
  if (type === "F") {
    return api.post("/api/v1/clientes", {
      createdBy,
      documento,
      name,
      contato,
      email,
      cep,
      estado,
      municipio,
      bairro,
      complemento,
      telefone,
      celular,
      vinculo_empresa,
      type,
    });
  } else {
    return api.post("/api/v1/clientes", {
      createdBy,
      documento,
      name,
      razao,
      email,
      cep,
      estado,
      municipio,
      bairro,
      complemento,
      telefone,
      celular,
      vinculo_empresa,
      type,
    });
  }
};










// ======================================================
// ROTAS EMPRESAS
// ======================================================

export const updateTempoEntregaEmpresa = async (
  userID,
  _id,
  id,
  tempoBalcaoMin,
  tempoBalcaoMax,
  tempoEntregaMin,
  tempoEntregaMax,
  tipo_impressao
) => {
  return api.post(`/api/v1/empresa/${_id}/tempo-entrega`, {
    tempoBalcaoMin,
    tempoBalcaoMax,
    tempoEntregaMin,
    tempoEntregaMax,
    tipo_impressao,
  });
};

export const updateTempoHorarioFuncionamento = async (
  userID,
  _id,
  id,
  status_loja,
  horario_funcionamento,
  timezone
) => {
  return api.post(`/api/v1/empresa/${_id}/horario-funcionamento`, {
    status_loja,
    horario_funcionamento,
    timezone,
  });
};

// ✅ MIGRADA PARA empresaV2 - Endpoint específico para tipo de região
export const updateTypeRegion = async (
  _id,               // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  id,                // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  userID,            // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  region_type_delivery
) => {
  return api.put("/api/v1/empresa/tipo-regiao", {
    region_type_delivery,
    type_of_region: region_type_delivery,
  });
};


export const updateEmpresa = async (
  id,
  userID,
  cnpj,
  name,
  razao,
  email,
  cep,
  estado,
  municipio,
  bairro,
  complemento,
  telefone,
  celular
) => {
  process.env.REACT_APP_ISDEVELOP === 'true' && console.log("caiu no updateEmpresa Normal");
  return api.put(`/api/v1/empresa/${id}`, {
    cnpj,
    name,
    razao,
    email,
    cep,
    estado,
    municipio,
    bairro,
    complemento,
    telefone,
    celular,
  });
};

export const updateEmpresaAddress = async (
  _id,
  id,
  userID,
  cep,
  estado,
  municipio,
  bairro,
  address_number,
  complemento,
  latitude,
  longitude,
  logradouro,
  country
) => {
  // V2: Usa endpoint seguro sem IDs na URL
  return api.put("/api/v1/empresa/endereco", {
    cep,
    country: country || null,
    estado,
    municipio,
    bairro,
    numero: address_number, // V2 espera 'numero' ao invés de 'address_number'
    logradouro: logradouro || "", // Campo obrigatório no V2
    complemento,
    latitude,
    longitude
  });
};

export const updateStatus = async (id_empresa, userID, status) => {
  // aqui id_empresa corresponde ao id_empresa numérico; endpoint v1 usa path param id
  return api.post(`/api/v1/empresa/${id_empresa}/status`, { status });
};

export const updateFormasPagamento = async (empresaId, formas_pagamento) => {
  return api.put(`/api/v1/empresa/${empresaId}/formas-pagamento`, { formas_pagamento });
};

export const updateConfiguracoesEntrega = async (empresaId, entrega_disabled, retirada_disabled) => {
  return api.put(`/api/v1/empresa/${empresaId}/configuracoes-entrega`, { entrega_disabled, retirada_disabled });
};


export const apiCheckImportStatus = async (idEmpresa) => {
  // ✅ V2 não precisa de idEmpresa - usa req.context automaticamente
  return api.get("/api/v1/empresa/importacao/status");
};

export const configurarResetPedido = async (id_empresa, dias_para_reset) => {
  return api.post(`/api/v1/empresa/${id_empresa}/configurar-reset-pedido`, { dias_para_reset });
};

export const resetPedidoManual = async (id_empresa) => {
  return api.post(`/api/v1/empresa/${id_empresa}/reset-pedido-counter`);
};

export const updateStatusBotEmpresa = async (id, status_bot) => {
  return api.patch(`/api/v1/empresa/${id}/status_bot`, { status_bot });
};

export const updateCallAtendenteEmpresa = async (id, call_atendente) => {
  return api.patch(`/api/v1/empresa/${id}/call_atendente`, { call_atendente });
};

export const importCardapioIfood = async (url_import, id_empresa) => {
  // ✅ V2 não precisa de id_empresa na URL - usa req.context automaticamente
  return api.post("/api/v1/empresa/importacao/ifood", { url_import });
};

export const importCardapioAnotaai = async (url_import, id_empresa) => {
  // ✅ V2 não precisa de id_empresa na URL - usa req.context automaticamente
  return api.post("/api/v1/empresa/importacao/anotaai", { url_import });
};

// ✅ MIGRADAS PARA empresaV2 - Usando req.context para segurança
export const getEmpresa = async () => {
  return api.get("/api/v1/empresa/info");
};

export const getVinculoEmpresa = async () => {
  return api.get("/api/v1/empresa/vinculo");
};
// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const getCompanyResponses = async () => {
  return api.get("/api/v1/empresa/custom-responses");
};


// ✅ MIGRADAS PARA empresaV2 - Usando req.context para segurança
// ✅ MIGRADA PARA empresaV2 - Suporte para criar e editar raios de entrega
export const updateRaioEntregaEmpresa = async (
  objIdEmpresa, // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  idEmpresa,    // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)  
  userID,       // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  raio_entrega,
  valor_entrega,
  _id = null    // Opcional: se fornecido = editar, se null = criar novo
) => {
  const payload = {
    raio_entrega,
    valor_entrega,
  };
  
  // Se tem ID, é para editar raio existente
  if (_id) {
    payload._id = _id;
  }

  return api.post("/api/v1/empresa/raio-entrega", payload);
};

// ✅ Incremento do raio de entrega em METROS (250|500|1000). Default 1000 = km inteiro
// (legado). Lojas em regiões pequenas (ex: Líbano) usam 250 p/ faixas de 250 em 250m.
export const updateRaioStepEmpresa = async (raio_entrega_step) => {
  return api.put("/api/v1/empresa/raio-step", { raio_entrega_step });
};

// ✅ Multi-moeda por empresa (Líbano: USD / LBP com cotação própria da loja).
// V2: sem IDs na URL, a empresa vem do req.context.
export const getCurrencySettings = async () => {
  return api.get("/api/v1/empresa/currency-settings");
};

// payload: { habilitado, base, exibicao, secundaria, cotacao }
// Com habilitado=false os demais campos são ignorados e a loja volta ao
// comportamento da região.
export const updateCurrencySettings = async (payload) => {
  return api.put("/api/v1/empresa/currency-settings", payload);
};

// ✅ Gorjeta / taxa de serviço por empresa (default 10%).
export const getTipSettings = async () => {
  return api.get("/api/v1/empresa/tip-settings");
};

// payload: { habilitado, percentual, modo: 'sugerida'|'fixa', aplica_em: ['mesa'] }
export const updateTipSettings = async (payload) => {
  return api.put("/api/v1/empresa/tip-settings", payload);
};

// ✅ MIGRADA PARA empresaV2 - Suporte para criar e editar bairros de entrega
export const updateBairroEntregaEmpresa = async (
  objIdEmpresa,     // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  idEmpresa,        // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)  
  userID,           // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  bairro_entrega,
  valor_entrega,
  _id = null        // Opcional: se fornecido = editar, se null = criar novo
) => {
  const payload = {
    bairro_entrega,
    valor_entrega,
  };
  
  // Se tem ID, é para editar bairro existente
  if (_id) {
    payload._id = _id;
  }
  
  return api.post("/api/v1/empresa/bairro-entrega", payload);
};
// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const apiUpdateImportFlag = async (importacao_finalizada) => {
  return api.post("/api/v1/empresa/update-import-flag", {
    importacao_finalizada
  });
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const updateEmpresaCancelamentoPedido = async (payload) => {
  return api.post("/api/v1/empresa/configurar-senha-cancelamento", payload);
};

// ✅ MIGRADA PARA empresaV2 - Configurar pedido mínimo
export const configurarPedidoMinimo = async (pedido_minimo) => {
  return api.post("/api/v1/empresa/configurar-pedido-minimo", { pedido_minimo });
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const deleteRaioEntrega = async (
  userID,          // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  idEmpresa,       // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)  
  id_raio_entrega  // ID do raio a ser deletado
) => {
  return api.delete(`/api/v1/empresa/raio-entrega/${id_raio_entrega}`);
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const deleteBairroEntrega = async (
  userID,             // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)
  idEmpresa,          // Parâmetro mantido para compatibilidade, mas não usado (V2 usa req.context)  
  id_bairro_entrega   // ID do bairro a ser deletado
) => {
  return api.delete(`/api/v1/empresa/bairro-entrega/${id_bairro_entrega}`);
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const addEnderecoEmpresa = async (enderecoData) => {
  return api.put("/api/v1/empresa/endereco", enderecoData);
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const toggleBotStatus = async (leadId) => {
  return api.put(`/api/v1/empresa/toggle-bot/${leadId}`);
};

// ===== NOVAS FUNÇÕES PARA CUSTOM RESPONSES =====

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const getCustomResponses = async () => {
  return api.get("/api/v1/empresa/custom-responses");
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const addCustomResponse = async (question, response) => {
  return api.post("/api/v1/empresa/custom-responses", {
    question,
    response,
  });
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const updateCustomResponse = async (responseId, question, response, active = true) => {
  return api.put(`/api/v1/empresa/custom-responses/${responseId}`, {
    question,
    response,
    active,
  });
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const deleteCustomResponse = async (responseId) => {
  return api.delete(`/api/v1/empresa/custom-responses/${responseId}`);
};

// ===== ORDERBOT CONFIG - Configurações do Bot de Pedidos =====

// ✅ V2 - Usando req.context para segurança
export const getOrderbotConfig = async () => {
  return api.get("/api/v1/empresa/orderbot-config");
};

// ✅ V2 - Usando req.context para segurança
export const updateOrderbotConfig = async (orderbot_enabled) => {
  return api.put("/api/v1/empresa/orderbot-config", { orderbot_enabled });
};

// ===== BOT CONTEXT - Descrição livre do restaurante p/ system prompt do bot =====

// ✅ V2 - Usando req.context para segurança
export const getBotContext = async () => {
  return api.get("/api/v1/empresa/bot-context");
};

// ✅ V2 - Usando req.context para segurança
export const updateBotContext = async (description) => {
  return api.put("/api/v1/empresa/bot-context", { description });
};

// ===== MENSAGENS AUTOMÁTICAS PERSONALIZADAS (WhatsApp) =====
// ✅ V2 - Usando req.context para segurança
export const getCustomOrderMessages = async () => {
  return api.get("/api/v1/empresa/custom-order-messages");
};

export const updateCustomOrderMessages = async (messages) => {
  return api.put("/api/v1/empresa/custom-order-messages", messages);
};

// ===== DADOS DA LOJA - Informações básicas da empresa =====

// ✅ V2 - Usando req.context para segurança
export const getDadosLoja = async () => {
  return api.get("/api/v1/empresa/dados-loja");
};

// ✅ V2 - Usando req.context para segurança
export const updateDadosLoja = async (dados) => {
  return api.put("/api/v1/empresa/dados-loja", dados);
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const changeStatusLoja = async (status_loja) => {
  return api.put("/api/v1/empresa/status-loja", { status_loja });
};

// ✅ MIGRADA PARA empresaV2 - Usando req.context para segurança
export const getBotStatusLead = (leadId) => {
  return api.get(`/api/v1/empresa/bot-status/${leadId}`);
};


// ✅ MIGRADAS PARA empresaV2 - Usando req.context para segurança
export const getProgressoConfiguracaoInicial = async () => {
  return api.get("/api/v1/empresa/progresso-configuracao");
};

export const atualizarProgressoConfiguracaoInicial = async (etapaCompleta, proximaEtapa, finalizada = false) => {
  return api.put("/api/v1/empresa/progresso-configuracao", {
    etapaCompleta,
    proximaEtapa,
    finalizada
  });
};

// Funções para solicitação de cardápio para equipe
// ✅ MIGRADA PARA rotasAdminV2 - Usando req.context para segurança
export const enviarCardapioParaEquipe = async (customLink, pdfFile, observacoes, userLookupName) => {
  const formData = new FormData();
  formData.append('customLink', customLink || '');
  formData.append('observacoes', observacoes || '');
  formData.append('userLookupName', userLookupName || '');

  if (pdfFile) {
    formData.append('pdfFile', pdfFile);
  }

  return api.post('/api/v1/empresa/enviar-cardapio-equipe', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// ✅ MIGRADA PARA rotasAdminV2 - Acesso apenas para Admin, lista todas as empresas
export const listarSolicitacoesCardapio = async (status = 'Todos', page = 1, limit = 10) => {
  return api.get("/api/v1/admin/solicitacoes-cardapio", {
    params: {
      status,
      page,
      limit
    }
  });
};

// ✅ MIGRADA PARA rotasAdminV2 - Acesso apenas para Admin
export const atualizarStatusSolicitacaoCardapio = async (solicitacaoId, status) => {
  return api.put(`/api/v1/admin/solicitacoes-cardapio/${solicitacaoId}/status`, {
    status
  });
};

// FUNÇÃO REMOVIDA: buscarSolicitacoesEmpresa - substituída por listarSolicitacoesCardapio













// ======================================================
// ROTAS PLANOS
// ======================================================

export const getPlansAsaas = async (userID) => {
  // V2: Não envia userID na URL - usa req.context automaticamente
  return api.get("/api/v1/planos/list-plans");
};

// Função para criar cliente Asaas
export const createAsaasCustomer = async (empresaId, dadosEndereco) => {
  // V2: Não envia empresaId na URL - usa req.context automaticamente
  return api.post("/api/v1/planos/createAsaasCustomer", dadosEndereco);
};


export const registerPlanoAdmin = async (
  createdBy,
  nome,
  plan_identifier,
  order,
  plan_cycle,
  maxPayments,
  valor_plano
) => {
  return api.post("/api/v1/planos/auth/registerPlanoAdmin", {
    createdBy,
    nome,
    plan_identifier,
    order,
    plan_cycle,
    maxPayments,
    valor_plano
  });
};

export const searchClienteAsaasAndGetInvoice = async (
  createdBy,
  email,
  name,
  cpf_cnpj,
  zip_code,
  number,
  plan_identifier,
  payment_type,
  isPromotional
) => {
  return api.post("/api/v1/planos/requestsToAsaas", {
    createdBy,
    email,
    name,
    cpf_cnpj,
    zip_code,
    number,
    plan_identifier,
    payment_type,
    isPromotional
  });
};

export const deleteAsaasPlan = async (
  createdBy,
  planId
) => {
  // V2: Não envia createdBy na URL - usa req.context automaticamente
  return api.post("/api/v1/planos/delete-plan-asaas", {
    planId
  });
}


export const criarAssinaturaAsaasBolPix = async (
  customerID,
  payment_type,
  cycle,
  value,
  nextDueDate
) => {
  return api.post("/api/v1/planos/criar-assinatura-asaas-bolpix", {
    customerID,
    payment_type,
    cycle,
    value,
    nextDueDate,
  });
};

export const criarAssinaturaAsaasCartao = async (
  customerID,
  nome_cliente,
  cpf_cnpj,
  cycle,
  numero_cartao,
  expiryMonth,
  expiryYear,
  ccv,
  email,
  postalCode,
  addressNumber,
  phone,
  value,
  nextDueDate,
  remoteIp
) => {
  return api.post("/api/v1/planos/criar-assinatura-asaas-cartao", {
    customerID,
    nome_cliente,
    cpf_cnpj,
    cycle,
    numero_cartao,
    expiryMonth,
    expiryYear,
    ccv,
    email,
    postalCode,
    addressNumber,
    phone,
    value,
    nextDueDate,
    remoteIp,
  });
};

export const criarCobrancaAsaas = async (
  customerID,
  nome_cliente,
  cpf_cnpj,
  numero_cartao,
  expiryMonth,
  expiryYear,
  ccv,
  email,
  postalCode,
  addressNumber,
  phone,
  value,
  nextDueDate,
  remoteIp,
  parcela
) => {
  return api.post("/api/v1/planos/criar-cobranca-asaas-cartao", {
    customerID,
    nome_cliente,
    cpf_cnpj,
    numero_cartao,
    expiryMonth,
    expiryYear,
    ccv,
    email,
    postalCode,
    addressNumber,
    phone,
    value,
    nextDueDate,
    remoteIp,
    parcela,
  });
};

export const criarCobrancaAsaasBolPix = async (
  customerID,
  payment_type,
  value,
  nextDueDate
) => {
  return api.post("/api/v1/planos/criar-cobranca-asaas", {
    customerID,
    payment_type,
    value,
    nextDueDate,
  });
};

export const listarAssinaturasAsaas = async (customer_id) => {
  const id_empresa = getEmpresaId();
  if (id_empresa) {
    return api.post("/api/v1/planos/lista-subscription-asaas", { customer_id, id_empresa });
  }
  return null;
};

export const getDataCustomerAsaas = async (customer_id) => {
  const id_empresa = getEmpresaId();
  if (id_empresa) {
    return api.post("/api/v1/planos/getData-customer-asaas", { customer_id, id_empresa });
  }
  return null;
}

// Verificar status da licença (baseado em faturas ativas do Asaas)
export const verificarLicencaAsaas = async () => {
  return api.get("/api/v1/assinaturas/verificar-licenca");
};

// Verificar status da licença local (baseado na data de vencimento no banco)
export const verificarLicencaLocal = async () => {
  return api.get("/api/v1/assinaturas/verificar-licenca-local");
};

export const getLastPendingInvoiceAsaas = async (subscriptionIdAsaas) => {
  const id_empresa = getEmpresaId();
  if (id_empresa) {
    return api.post("/api/v1/planos/lastPendingInvoice", { subscriptionIdAsaas, id_empresa });
  }
  return null;
}

export const getAllInvoicesAsaas = async (subscriptionIdAsaas) => {
  const id_empresa = getEmpresaId();
  if (id_empresa) {
    return api.post("/api/v1/planos/allInvoices", { subscriptionIdAsaas, id_empresa });
  }
  return null;
};















// ======================================================
// ROTAS WHATSAPP
// ======================================================

// Função para buscar e salvar imagem de perfil do WhatsApp
export const getWhatsappProfilePicture = async (leadChannelID) => {
  return api.post(`/api/v1/whatsapp/get-profile-picture/${leadChannelID}`);
};

// Função para buscar imagem de perfil do próprio usuário (dono da instância)
export const getMyWhatsappProfilePicture = async (empresaID) => {
  return api.get(`/api/v1/whatsapp/get-my-profile-picture/${empresaID}`);
};

// Função para forçar busca e salvamento do JID
export const fetchWhatsappJID = async (empresaID) => {
  return api.post(`/api/v1/whatsapp/fetch-jid/${empresaID}`);
};

export const getQrCodeWhatsapp = async (empresaID) => {
  return api.get("/api/v1/whatsapp/qrcode/" + empresaID);
};

// **📌 NOVA FUNÇÃO PARA RENOVAR QR CODE VIA API**
export const renewQrCodeWhatsapp = async (empresaID) => {
  return api.post("/api/v1/whatsapp/qrcode/renew/" + empresaID);
};

export const refreshProfilePicturesWhatsapp = async (empresaID) => {
  return api.post("/api/v1/whatsapp/refresh-profile-pictures/" + empresaID);
};

export const refreshContactInfoWhatsapp = async (leadChannelID) => {
  return api.post("/api/v1/whatsapp/refresh-contact-info/" + leadChannelID);
};

// **📌 FUNÇÃO PARA ATUALIZAR NOME DO CONTATO MANUALMENTE**
export const updateContactName = async (empresaID, leadID, name) => {
  return api.patch(`/api/v1/whatsapp/chats/${empresaID}/lead/${leadID}/name`, { name });
};

// **📌 NOVA FUNÇÃO PARA VERIFICAR STATUS DE CONEXÃO DA EVOLUTION API**
export const getWhatsappConnectionStatus = async (empresaID) => {
  return api.get("/api/v1/whatsapp/connectionStatus/" + empresaID);
};

// 🔥 NOVO: Funções para gerenciar fila de atendimento humano
export const buscarFilaAtendimento = async (empresaId) => {
  return api.get(`/api/v1/whatsapp/fila-atendimento/${empresaId}`);
};

export const iniciarAtendimentoFila = async (empresaId, atendimentoId, userData) => {
  return api.post(`/api/v1/whatsapp/fila-atendimento/${empresaId}/iniciar/${atendimentoId}`, userData);
};

export const finalizarAtendimentoFila = async (empresaId, atendimentoId, observacoes) => {
  return api.post(`/api/v1/whatsapp/fila-atendimento/${empresaId}/finalizar/${atendimentoId}`, { observacoes });
};

export const cancelarAtendimentoFila = async (empresaId, atendimentoId, motivo) => {
  return api.delete(`/api/v1/whatsapp/fila-atendimento/${empresaId}/cancelar/${atendimentoId}`, { data: { motivo } });
};

export const resolverTodosAtendimentos = async (empresaId) => {
  return api.post(`/api/v1/whatsapp/fila-atendimento/${empresaId}/resolver-todos`);
};

export const limparAtendimentosFinalizados = async (empresaId) => {
  return api.post(`/api/v1/whatsapp/fila-atendimento/${empresaId}/limpar-finalizados`);
};

export const sendTextMessageWhatsapp = async (empresaID, number, message) => {
  //setAuthToken(token);
  return api.post("/api/v1/whatsapp/sendTextMessage/" + empresaID, { number, message });
};

export const removeWhatsappSession = async (empresaID) => {
  return api.get(`/api/v1/whatsapp/removeSession/${empresaID}`);
};

// Função para configurar webhook
export const configureWhatsappWebhook = async (empresaID) => {
  return api.post(`/api/v1/whatsapp/webhook/configure/${empresaID}`);
};

// Função para verificar status do webhook
export const getWhatsappWebhookStatus = async (empresaID) => {
  return api.get(`/api/v1/whatsapp/webhook/status/${empresaID}`);
};

// Função para obter os chats
export const getWhatsappChats = async (
  empresaID,
  page,
  pageSize,
  query
) => {
  return api.get(`/api/v1/whatsapp/chats/${empresaID}`, {
    params: {
      page,
      pageSize,
      query,
    },
  });
};

// Função para obter os conversa de um chat com LeadId
export const getWhatsappChatLead = async (
  empresaID,
  leadID,
  page,
  pageSize
) => {
  return api.get(`/api/v1/whatsapp/chats/${empresaID}/messages/${leadID}`, {
    params: {
      page,
      pageSize,
    },
  });
};

// Função para obter contagem de mensagens não lidas
export const getWhatsappUnreadCount = async (empresaID) => {
  return api.get(`/api/v1/whatsapp/chats/${empresaID}/unread-count`);
};

// Função para marcar mensagens como lidas
export const markWhatsappMessagesAsRead = async (empresaID, leadID) => {
  return api.put(`/api/v1/whatsapp/chats/${empresaID}/messages/${leadID}/mark-as-read`);
};

// 🔥 NOVO: Função para buscar um chat específico por lead_id
export const getWhatsappChatById = async (empresaID, leadID) => {
  return api.get(`/api/v1/whatsapp/chats/${empresaID}/chat/${leadID}`);
};

// 🔥 NOVO: Função para iniciar nova conversa com um contato
export const createWhatsappChat = async (empresaID, contactNumber, contactName) => {
  return api.post(`/api/v1/whatsapp/chats/${empresaID}/create`, {
    contactNumber,
    contactName
  });
};

export const deleteLeadMessages = async (empresaID, leadID, token) => {
  return api.delete(`/api/v1/whatsapp/messages/${empresaID}/${leadID}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
};














// ======================================================
// ROTAS CATALOGO/CARDÁPIO
// ======================================================

export const updateCategoriasOrder = async (
  userID,
  _id,
  id_categoria,
  id_empresa,
  order
) => {
  return api.put(`/api/v1/catalogo/categorias/${_id}/order`, {
    order,
  });
};

export const updateItensOrder = async (
  userID,
  _id,
  id_item,
  id_empresa,
  order
) => {
  return api.put(`/api/v1/catalogo/itens/${_id}`, { order }); // Usar ObjectId e remover campos desnecessários
};

// Reordenar adicionais (sub-itens) dentro de um grupo — drag-and-drop (Tipo B)
export const updateAdicionaisItemOrder = async (id_grupo, adicional_ids) => {
  return api.put(`/api/v1/catalogo/grupos-adicionais/${id_grupo}/adicionais/order`, { adicional_ids });
};

// Reordenar os grupos de adicionais vinculados a um item — drag-and-drop (Tipo A)
export const updateGruposDoItemOrder = async (id_item, grupo_ids) => {
  return api.put(`/api/v1/catalogo/itens/${id_item}/grupos-adicionais/order`, { grupo_ids });
};

export const updateItemOut = async (userID, _id, id_item, id_empresa, out) => {
  return api.put(`/api/v1/catalogo/itens/${_id}/out`, { out });
};

export const updateItemOutSalao = async (userID, _id, id_item, id_empresa, out) => {
  return api.put(`/api/v1/catalogo/itens/${_id}/out`, { out_salao: out });
};


export const updateItemPrice = async (
  userID,
  _id,
  id_item,
  id_empresa,
  price
) => {
  return api.put(`/api/v1/catalogo/itens/${_id}`, { price }); // Usar ObjectId e remover campos desnecessários
};

export const updateItemPriceSalao = async (
  userID,
  _id,
  id_item,
  id_empresa,
  price
) => {
  return api.put(`/api/v1/catalogo/itens/${_id}`, { price_salao: price }); // Usar ObjectId e remover campos desnecessários
};

export const updateCategorias = async (
  userID,
  _id,
  id_categoria,
  id_empresa,
  modelo,
  title,
  disponibilidade,
  dia_horario_disponibilidade
) => {
  return api.put(`/api/v1/catalogo/categorias/${_id}`, {
    modelo,
    title,
    disponibilidade,
    dia_horario_disponibilidade,
  });
};

export const updateAdicionaisGroup = async ({
  userID,
  adicional_objId,
  idEmpresa,
  title,
  minimo,
  maximo,
  mandatory,
  calcularMaiorValor,
  calcularMedia,
  precificacao,
  type
}) => {
  return api.put(`/api/v1/catalogo/grupos-adicionais/${adicional_objId}`, {
    //id_empresa: idEmpresa,
    title,
    min: minimo,
    max: maximo,
    mandatory,
    calcular_maior_valor: calcularMaiorValor,
    calcular_media: calcularMedia,
    precificacao,
    type
  });
};

// ✅ Vincular itens em lote a um grupo de adicionais
export const vincularItensAoGrupo = async (grupoId, item_ids) => {
  return api.put(`/api/v1/catalogo/grupos-adicionais/${grupoId}/vincular-itens`, { item_ids });
};

export const updateItens = async (
  userID,
  itemObjId,
  id_item,
  idEmpresa,
  orderItem,
  category_item_id,
  category_item_title,
  title,
  description,
  out,
  images,
  price,
  has_adicional,
  adicionais,
  type
) => {
  const body = {
    order: orderItem,
    category_item_id,
    category_item_title,
    title,
    description,
    out,
    price,
    has_adicional,
    adicionais,
    type,
  };

  // Somente incluir images no body se foi explicitamente fornecido (não null/undefined)
  // null/undefined = não alterar imagem; [] ou base64 = alterar/remover imagem
  if (images !== null && images !== undefined) {
    body.images = images;
  }

  return api.put(`/api/v1/catalogo/itens/${itemObjId}`, body);
};

export const removeAdicionalFromItem = async (
  userID,
  itemId,
  grupoAdicionalValue
) => {
  // V2: Backend espera campo "grupoId" no body
  return api.patch(`/api/v1/catalogo/itens/${itemId}/remove-grupo`, { grupoId: grupoAdicionalValue });
};

export const deleteCategoria = async (userID, id) => {
  return api.delete(`/api/v1/catalogo/categorias/${id}`);
};

export const deleteGrupoAdicional = async (userID, id) => {
  return api.delete(`/api/v1/catalogo/grupos-adicionais/${id}`);
};

// V2: Duplicar grupo de adicionais/sabores com todos os itens
export const duplicateGrupoAdicional = async (id_grupo) => {
  return api.post(`/api/v1/catalogo/grupos-adicionais/${id_grupo}/duplicate`);
};

export const deleteItem = async (userID, id) => {
  // V2: Não precisa enviar userID - usa req.context automaticamente
  return api.delete(`/api/v1/catalogo/itens/${id}`);
};

export const registerCategoria = async (
  createdBy,
  id_empresa,
  title,
  disponibilidade,
  dia_horario_disponibilidade,
  only_pdv,
  only_qrcode,
  only_delivery_take_local,
  modelo
) => {
  return api.post("/api/v1/catalogo/categorias", {
    createdBy,
    id_empresa,
    title,
    disponibilidade,
    dia_horario_disponibilidade,
    only_pdv,
    only_qrcode,
    only_delivery_take_local,
    modelo
  });
};

export const registerItem = async (
  createdBy,
  id_empresa,
  category_item_id,
  category_item_title,
  title,
  description,
  out,
  image,
  price,
  disponibilidade,
  type
) => {
  return api.post("/api/v1/catalogo/itens", {
    createdBy,
    id_empresa,
    category_item_id,
    category_item_title,
    title,
    description,
    out,
    image,
    price,
    disponibilidade,
    type
  });
};

export const registerGrupoAdicionais = async (
  createdBy,
  id_empresa,
  title,
  min,
  max,
  mandatory,
  out,
  calcular_maior_valor,
  calcular_media,
  precificacao,
  type
) => {
  return api.post("/api/v1/catalogo/grupos-adicionais", {
    //createdBy,
    //id_empresa,
    title,
    min,
    max,
    mandatory,
    out,
    calcular_maior_valor,
    calcular_media,
    precificacao,
    type
  });
};

export const registerAdicionais = async (
  createdBy,
  id_grupo,
  id_empresa,
  title,
  price,
  out,
  image
) => {
  return api.post("/api/v1/catalogo/registerSubAdicionais", {
    //createdBy,
    id_grupo,
    //id_empresa,
    title,
    price,
    out,
    image,
  });
};

export const updateItemAdicional = async (data) => {
  return api.patch("/api/v1/catalogo/updateSubAdicional", data);
};


// ✅ MIGRADAS PARA catalogoV2 - Usando req.context para segurança
export const deleteItemAdicional = async (data) => {
  return api.delete("/api/v1/catalogo/subadicional", { data });
};

export const getCategorias = async () => {
  return api.get("/api/v1/catalogo/categorias-private");
};

export const getAdicionais = async () => {
  return api.get("/api/v1/catalogo/adicionais-private");
};

export const getItens = async (categoria_id = null, search = '') => {
  const params = new URLSearchParams();
  if (categoria_id) params.append('categoria_id', categoria_id);
  if (search) params.append('search', search);

  return api.get(`/api/v1/catalogo/itens-private?${params.toString()}`);
};

export const getItemPrivate = async (itemObjId) => {
  return api.get(`/api/v1/catalogo/item/${itemObjId}`);
};

















// ✅ MIGRADAS PARA rotasAdminV2 - Usando req.context para segurança
// filters: { situacao, whatsapp, plano, dataInicio, dataFim }
export const getEmpresasAdmin = async (page, limit, search = '', filters = {}) => {
  return api.get("/api/v1/admin/empresas", {
    params: { page, limit, search, ...filters }
  });
};

// Faturas sob demanda (Asaas) — chamado ao abrir "Ver faturas" de uma empresa.
export const getFaturasAdmin = async (empresaObjId) => {
  return api.get(`/api/v1/admin/empresa/${empresaObjId}/faturas`);
};

export const createInstanceAdmin = async (empresaId) => {
  return api.post(`/api/v1/admin/whatsapp/create-instance/${empresaId}`);
};

// ✅ MIGRADA PARA impressaoV2 - Usando req.context para segurança
export const testeImpressao = async (companyId) => {
  return api.post("/api/v1/impressao/teste", { companyId });
}


// ✅ MIGRADA PARA relatoriosV2 - Usando req.context para segurança
export const getClientesAtivos = async (startDate, endDate) => {
  // Garantir que as datas são objetos Date válidos
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  
  return api.get("/api/v1/relatorios/clientes-ativos", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ MIGRADA PARA usersV2 - Usando req.context para segurança
export const updateUser = async (
  email,
  password,
  name
) => {
  return api.put("/api/v1/users/update", {
    email,
    password,
    name
  });
};

// 🛠️ DEBUG E DIAGNÓSTICO DE ASSINATURAS (Admin apenas)
export const debugAssinaturas = async () => {
  return api.get("/api/v1/planos/debug-assinaturas");
};

export const cleanupAssinaturasOrfas = async (empresaId = null) => {
  return api.post("/api/v1/planos/cleanup-assinatura-orfas", { empresaId });
};

export const testWebhookSubscriptionDeleted = async (subscriptionId) => {
  return api.post("/api/v1/planos/test-webhook-subscription-deleted", { subscriptionId });
};


// ✅ MIGRADA PARA entregadoresV2 - Usando req.context para segurança
export const updateEntregador = async (
  idToEdit,
  name,
  veiculo,
  telefone,
  placa
) => {
  return api.put(`/api/v1/entregadores/${idToEdit}`, {
    name,
    veiculo,
    telefone,
    placa,
  });
};


// ✅ MIGRADAS PARA catalogoV2 - Usando req.context para segurança
export const updateItemAdicionalOut = async (userID, idGrupo, itemID, titulo, price, id_item, idEmpresa, newOutValue) => {
  return api.patch("/api/v1/catalogo/updateSubAdicional", {
    _id: itemID,
    id_grupo: idGrupo,
    title: titulo,
    price: price,
    id_adicional: id_item,
    out: newOutValue,
  });
};

export const updateItemAdicionalOutSalao = async (userID, idGrupo, itemID, titulo, price, id_item, idEmpresa, newOutValue) => {
  return api.patch("/api/v1/catalogo/updateSubAdicional", {
    _id: itemID,
    id_grupo: idGrupo,
    title: titulo,
    price: price,
    id_adicional: id_item,
    out_salao: newOutValue,
  });
};

// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const updateStatusPedido = async (
  userID, // Mantido para compatibilidade, mas não usado (usa req.context)
  _id,
  id_pedido, // Mantido para compatibilidade, mas não usado
  status_pedido
) => {
  return api.put(`/api/v1/pedidos/${_id}/status`, {
    status_pedido,
  });
};

// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const updatePedidoWithEntregador = async (
  _id,
  entregador
) => {
  return api.put(`/api/v1/pedidos/${_id}/entregador`, {
    entregador,
  });
};


// ✅ MIGRADA PARA impressaoV2 - Usando req.context para segurança
export const ImprimirPedido = async (dadosPedido) => {
  return api.post("/api/v1/impressao/pedido", {
    dadosPedido,
  });
};

// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const updateStatusPedidoFinalizado = async (
  userID, // Mantido para compatibilidade, mas não usado (usa req.context)
  _id,
  id_pedido, // Mantido para compatibilidade, mas não usado
  newStatus, // Mantido para compatibilidade, mas não usado
  finalizadoAt // Mantido para compatibilidade, mas não usado
) => {
  return api.put(`/api/v1/pedidos/${_id}/finalizar`);
};

// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const updateStatusPrint = async (_id, id_pedido) => {
  return api.put(`/api/v1/pedidos/${_id}/update-status-print`, {
    id_pedido
  });
};

// ✅ MIGRADA PARA usersV2 - Usando req.context para segurança
export const updateUserImg = async (image) => {
  return api.put("/api/v1/users/update-image", { image });
};

// ✅ MIGRADA PARA entregadoresV2 - Usando req.context para segurança
export const deleteEntregador = async (id) => {
  return api.delete(`/api/v1/entregadores/${id}`);
};

// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const cancelarPedido = async (id, senha) => {
  return api.post(`/api/v1/pedidos/${id}/cancelar`, { senha });
};

// ======================================================
// ROTAS PÚBLICAS DE RASTREAMENTO DE PEDIDOS
// ======================================================

// Buscar pedidos por session_token (para clientes anônimos) - ROTA PÚBLICA
export const trackPedidosByToken = async (session_token) => {
  return api.get(`/track-pedidos/${session_token}`);
};

// Buscar pedido específico por session_token e id_pedido - ROTA PÚBLICA  
export const trackPedidoDetails = async (session_token, id_pedido) => {
  return api.get(`/track-pedidos/${session_token}/${id_pedido}`);
};



// ✅ MIGRADA PARA rotasAdminV2 - Usando req.context para segurança
export const deleteEmpresaCompleta = async (empresaObjId, usuario, senha) => {
  return api.delete(`/api/v1/admin/empresa/${empresaObjId}/delete-complete`, {
    data: { usuario, senha }
  });
};

// Ativar/Inativar empresa (admin). inativo=true bloqueia logins/operações de todos
// os usuários da empresa; inativo=false reativa. Reversível por 1 clique.
export const toggleEmpresaAtiva = async (empresaObjId, inativo) => {
  return api.patch(`/api/v1/admin/empresa/${empresaObjId}/toggle-active`, { inativo });
};

// =============================================
// ADMIN - Manual Subscription Management
// =============================================
export const updateSubscriptionAdmin = async (empresaObjId, data) => {
  return api.put(`/api/v1/admin/empresa/${empresaObjId}/subscription`, data);
};

export const addPaymentAdmin = async (empresaObjId, data) => {
  return api.post(`/api/v1/admin/empresa/${empresaObjId}/payment`, data);
};

export const getPaymentsAdmin = async (empresaObjId, page = 1, limit = 10) => {
  return api.get(`/api/v1/admin/empresa/${empresaObjId}/payments`, {
    params: { page, limit }
  });
};

// ✅ MIGRADA PARA entregadoresV2 - Usando req.context para segurança
export const registerEntregador = async (
  nome,
  telefone,
  veiculo,
  placa
) => {
  return api.post("/api/v1/entregadores", {
    name: nome, // Usar 'name' para coincidir com o modelo do banco
    telefone,
    veiculo,
    placa,
  });
};


// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const updatePedidoFromPdv = async (
  objIdPedido,
  itens,
  celular_cliente,
  nome_cliente,
  tipo_pagamento,
  entrega,
  desconto,
  cpf_cnpj,
  valor_troco,
  valor_total,
  descricao,
  external_id
) => {
  return api.put("/api/v1/pedidos/update-from-pdv", {
    objIdPedido,
    itens,
    celular_cliente,
    nome_cliente,
    tipo_pagamento,
    entrega,
    desconto,
    cpf_cnpj,
    valor_troco,
    valor_total,
    descricao,
    external_id
  });
};

// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const updatePedidoFromMesas = async (
  objIdPedido,
  itens,
  celular_cliente,
  nome_cliente,
  tipo_pagamento,
  entrega,
  desconto,
  cpf_cnpj,
  valor_troco,
  valor_total,
  descricao,
  mesa_id,
  mesa_numero
) => {
  return api.put("/api/v1/pedidos/update-from-mesas", {
    objIdPedido,
    itens,
    celular_cliente,
    nome_cliente,
    tipo_pagamento,
    entrega,
    desconto,
    cpf_cnpj,
    valor_troco,
    valor_total,
    descricao,
    mesa_id,
    mesa_numero
  });
};


// ✅ Atualizar apenas forma de pagamento (sem impressão de comanda)
export const updateTipoPagamento = async (pedidoId, tipo_pagamento, valor_troco = 0) => {
  return api.patch(`/api/v1/pedidos/${pedidoId}/tipo-pagamento`, { tipo_pagamento, valor_troco });
};

// ✅ MIGRADA PARA pedidosV2 - Usando req.context para segurança
export const getUltimoPedidoID = async () => {
  return api.get("/api/v1/pedidos/ultimo-id");
}

// ✅ MIGRADA PARA assinaturasV2 - Usando req.context para segurança
export const getDaysToExpireLicense = async () => {
  return api.get("/api/v1/assinaturas/dias-para-expirar");
}

// ✅ MIGRADA PARA rotasAdminV2 - Usando req.context para segurança
export const deleteInstanceAdmin = async (id) => {
  return api.delete("/api/v1/admin/whatsapp/delete-instance", { data: { id } });
};

// ✅ MIGRADA PARA assinaturasV2 - Usando req.context para segurança
export const checkLicense = () => {
  return api.get("/api/v1/assinaturas/verificar-licenca");
};

// ✅ MIGRADA PARA ibgeV2 - Usando req.context para segurança
export const getBairros = async (city, uf) => {
  return api.get("/api/v1/ibge/bairros", {
    params: { city, uf }
  });
};

// ✅ MIGRADAS PARA mesasV2 - Usando req.context para segurança
export const getMesas = async () => {
  const { data } = await api.get('/api/v1/mesas');

  if (!data.status) {
    return null;
  }

  return data.mesas;
}

export const createMesa = async ({ name, qr_code, number }) => {
  const { data } = await api.post('/api/v1/mesas', {
    name,
    qr_code,
    number
  });

  if (!data.status) {
    return null;
  }

  return data.mesa;
}

// ✅ MIGRADA PARA mesasV2 - Renomear mesa
export const renameMesa = async ({ mesaId, number, name }) => {
  return api.put(`/api/v1/mesas/${mesaId}`, { number, name });
}

// ✅ MIGRADA PARA mesasV2 - Usando req.context para segurança
export const createManyMesa = async ({ url, quantity, namePrefix }) => {
  const { data } = await api.post('/api/v1/mesas/many', {
    url,
    quantity,
    namePrefix
  });

  if (!data.status) {
    return null;
  }

  return data.mesas;
}

// ✅ MIGRADA PARA mesasV2 - Usando req.context para segurança
export const registerPedidoMesa = async (
  {
    mesa_id,
    itens,
    celular_cliente,
    nome_cliente,
    valor_total,
    observacoes,
    quantidade_pessoas,
  }
) => {
  return api.post("/api/v1/mesas/pedido", {
    mesa_id,
    itens,
    celular_cliente,
    nome_cliente,
    valor_total,
    observacoes,
    quantidade_pessoas,
  });
};

// ✅ MIGRADA PARA mesasV2 - Adicionar cliente/grupo à mesa
export const addClientToMesa = async ({ mesa_id, nome, celular, quantidade_pessoas }) => {
  return api.post("/api/v1/mesas/add-client", { mesa_id, nome, celular, quantidade_pessoas });
};

// ✅ MIGRADA PARA mesasV2 - Usando req.context para segurança
export const deleteMesa = async ({ mesaId }) => {
  const { data } = await api.delete(`/api/v1/mesas/${mesaId}`);

  if (!data.status) {
    return null;
  }

  return data;
}

// ✅ MIGRADA PARA mesasV2 - Usando req.context para segurança
export const registerPayment = async (
  {
    mesa_id,
    payment_type,
    total_payed,
    customer_name,
    customer_phone,
    paid_items,
    desconto,
    gorjeta
  }
) => {
  return api.post("/api/v1/mesas/pagamento", {
    mesa_id,
    payment_type,
    // `total_payed` e SO o consumo -- e ele que abate a conta. A gorjeta vai em
    // campo proprio: somada aqui, a mesa pareceria mais paga do que esta e o
    // proximo a pagar sairia pagando a menos.
    total_payed,
    customer_name,
    customer_phone,
    paid_items,
    // `desconto` era descartado no destructuring: o modal enviava e nunca
    // chegava ao backend. O backend usa esse campo como rede de seguranca para o
    // caso de o PUT /desconto ainda nao ter persistido quando o pagamento sai.
    desconto,
    gorjeta
  });
};

// Destrava mesa já paga (caso backend tenha travado por drift de centavos).
// Backend só libera se houver cobertura total (valor com tolerância OU itens).
export const forceCloseMesa = async (mesa_id) => {
  return api.post(`/api/v1/mesas/${mesa_id}/force-close`);
};

// Atualiza o desconto aplicado no fechamento da mesa.
export const updateMesaDesconto = async (mesa_id, desconto) => {
  return api.put(`/api/v1/mesas/${mesa_id}/desconto`, { desconto });
};

// Impressão de mesa/pedido de mesa.
//
// O payload da comanda é montado NO BACKEND (mesasV2), e não aqui, por três
// motivos que já custaram bug: pedido de mesa não tem `entrega` (montar no
// front quebrava lendo `entrega.tipo_entrega`), a moeda/cotação precisa vir do
// snapshot do pedido e o idioma da comanda vem do `preferredLanguage` da
// empresa. Mesmos endpoints que o garcomapp já usa.
export const imprimirResumoMesa = async (mesa_id) => {
  return api.post("/api/v1/mesas/print", { mesa_id });
};

export const imprimirPedidoMesa = async (pedido_id) => {
  return api.post("/api/v1/mesas/print-pedido", { pedido_id });
};


// ✅ MIGRADAS PARA empresaV2 - Usando req.context para segurança
export const getProgressoImportacao = async () => {
  return api.get("/api/v1/empresa/progresso-importacao");
};

export const resetImportStatus = async () => {
  return api.post("/api/v1/empresa/reset-import-status");
};



// ====================================================================
// FUNÇÕES DE API PARA MÓDULO DE ESTOQUE
// ====================================================================

// Funções para controle de estoque de itens
// ✅ MIGRADAS PARA estoqueV2 - Usando req.context para segurança
export const getEstoqueItens = async () => {
  return api.get("/api/v1/estoque/itens");
};

export const updateEstoqueItem = async (itemId, dadosEstoque) => {
  // V2: userID não é mais usado - usa req.context automaticamente
  return api.post("/api/v1/estoque/item", {
    itemId,
    ...dadosEstoque
  });
};

// ✅ MIGRADAS PARA estoqueV2 - Usando req.context para segurança
export const getMateriasPrimas = async () => {
  return api.get("/api/v1/estoque/materias-primas");
};

export const createMateriaPrima = async (dadosMateriaPrima) => {
  return api.post("/api/v1/estoque/materia-prima", dadosMateriaPrima);
};

export const updateMateriaPrima = async (materiaPrimaId, dadosMateriaPrima) => {
  return api.put(`/api/v1/estoque/materia-prima/${materiaPrimaId}`, dadosMateriaPrima);
};

export const deleteMateriaPrima = async (materiaPrimaId) => {
  return api.delete(`/api/v1/estoque/materia-prima/${materiaPrimaId}`);
};

// ✅ MIGRADAS PARA estoqueV2 - Usando req.context para segurança
export const getConfiguracoesEstoque = async () => {
  return api.get("/api/v1/estoque/configuracoes");
};

export const updateConfiguracoesEstoque = async (configuracoes) => {
  return api.post("/api/v1/estoque/configuracoes", configuracoes);
};

// Receitas (composição)
export const updateItemComposicao = async (itemId, composicao) => {
  return api.put(`/api/v1/estoque/item/${itemId}/composicao`, { composicao });
};

export const getItemComposicao = async (itemId) => {
  return api.get(`/api/v1/estoque/item/${itemId}/composicao`);
};

export const updateAdicionalComposicao = async (grupoId, adicionalId, composicao) => {
  return api.put(`/api/v1/estoque/adicional/${grupoId}/opcao/${adicionalId}/composicao`, { composicao });
};

export const getAdicionaisComReceita = async () => {
  return api.get("/api/v1/estoque/adicionais-com-receita");
};

// Movimentações de estoque
export const getMovimentacoesEstoque = async (params) => {
  return api.get("/api/v1/estoque/movimentacoes", { params });
};

export const registrarEntradaEstoque = async (materiaPrimaId, dados) => {
  return api.post(`/api/v1/estoque/materia-prima/${materiaPrimaId}/entrada`, dados);
};

export const registrarAjusteEstoque = async (materiaPrimaId, dados) => {
  return api.post(`/api/v1/estoque/materia-prima/${materiaPrimaId}/ajuste`, dados);
};

// Movimentações de itens do cardápio
export const registrarEntradaItem = async (itemId, dados) => {
  return api.post(`/api/v1/estoque/item/${itemId}/entrada`, dados);
};

export const registrarAjusteItem = async (itemId, dados) => {
  return api.post(`/api/v1/estoque/item/${itemId}/ajuste`, dados);
};

// Saídas manuais de estoque
export const registrarSaidaEstoque = async (materiaPrimaId, dados) => {
  return api.post(`/api/v1/estoque/materia-prima/${materiaPrimaId}/saida`, dados);
};

export const registrarSaidaItem = async (itemId, dados) => {
  return api.post(`/api/v1/estoque/item/${itemId}/saida`, dados);
};

// Alertas de estoque
export const getAlertasEstoque = async () => {
  return api.get("/api/v1/estoque/alertas");
};

// Validação pré-pedido
export const validarEstoquePedido = async (itens) => {
  return api.post("/api/v1/estoque/validar-pedido", { itens });
};

// ========================================
// ✅ TUNA PAYMENTS - Pagamentos Online
// ========================================

// Merchant Status (GET /api/v1/tuna/merchant)
export const getTunaMerchantStatus = async () => {
  const response = await api.get("/api/v1/tuna/merchant");
  return response.data;
};

// Ativar/Criar Merchant (POST /api/v1/tuna/merchant/create)
export const activateTunaMerchant = async (activationData) => {
  const response = await api.post("/api/v1/tuna/merchant/create", activationData);
  return response.data;
};

// Desativar Merchant (PUT /api/v1/tuna/merchant/disable)
export const deactivateTunaMerchant = async () => {
  const response = await api.put("/api/v1/tuna/merchant/disable");
  return response.data;
};

// Atualizar métodos de pagamento
export const updateTunaPaymentMethods = async (paymentMethods) => {
  // Backend espera { payment_methods: { pix: true, credit_card: false } }
  const response = await api.put("/api/v1/tuna/merchant/payment-methods", {
    payment_methods: paymentMethods
  });
  return response.data;
};

// ========================================
// KYC (Know Your Customer) - Status de aprovação
// ========================================

// Obter status KYC do merchant (PIX e Cartão)
// Usado para verificar se cartão pode ser habilitado
export const getTunaKycStatus = async () => {
  const response = await api.get("/api/v1/tuna/merchant/kyc-status");
  return response.data;
};

// Sincronizar status KYC com a Tuna (chama API GetMerchant)
export const syncTunaKycStatus = async () => {
  const response = await api.post("/api/v1/tuna/merchant/sync-kyc");
  return response.data;
};

// ========================================
// Pagamentos
// ========================================

// Criar pagamento PIX (autenticado)
export const createPixPayment = async (paymentData) => {
  const response = await api.post("/api/v1/tuna/pix/create", paymentData);
  return response.data;
};

// Criar pagamento PIX (público - cardápio)
export const createPixPaymentPublic = async (paymentData) => {
  const response = await api.post("/tuna/payment/pix", paymentData);
  return response.data;
};

// Verificar status do pagamento (autenticado)
export const getPaymentStatus = async (transactionId) => {
  const response = await api.get(`/api/v1/tuna/payment/${transactionId}/status`);
  return response.data;
};

// Verificar status do pagamento (público - para cardápio)
export const getPaymentStatusPublic = async (transactionId) => {
  const response = await api.get(`/tuna/payment/${transactionId}/status`);
  return response.data;
};

// Obter sessionId para Tuna SDK (público - tokenização de cartão)
export const getTunaSession = async (idEmpresa, customer = {}) => {
  const response = await api.post("/tuna/session", {
    id_empresa: idEmpresa,
    customer
  });
  return response.data;
};

// Criar pagamento com cartão (público - cardápio)
export const createCardPaymentPublic = async (paymentData) => {
  const response = await api.post("/tuna/payment/card", paymentData);
  return response.data;
};

// Obter configuração Tuna da empresa (público - cardápio)
export const getTunaConfigPublic = async (idEmpresa) => {
  const response = await api.get(`/tuna/merchant/${idEmpresa}`);
  return response.data;
};

// ✅ Verificar se existe pedido pendente de CartaoOnline (público - cardápio)
export const checkPendingCardOrder = async (idEmpresa, celularCliente) => {
  const response = await apiPublic.post("/check-pending-card-order", {
    id_empresa: idEmpresa,
    celular_cliente: celularCliente
  });
  return response.data;
};

// ✅ Cancelar pedido expirado ou com valor alterado automaticamente (público - cardápio)
// Usado quando:
// - O tempo de pagamento expira (30 min para PIX/Cartão)
// - O valor do carrinho/entrega muda (force=true)
export const cancelExpiredOrder = async (pedidoId, idEmpresa, motivo = null, force = false) => {
  try {
    const response = await apiPublic.post(`/cancel-expired-order/${pedidoId}`, {
      id_empresa: idEmpresa,
      motivo: motivo || "Pagamento não realizado - tempo expirado",
      force: force // Se true, pula validação de tempo (usado quando valor mudou)
    });
    return response.data;
  } catch (error) {
    console.error('[API] Erro ao cancelar pedido expirado:', error);

    // ✅ IMPORTANTE: Se for 409 (pagamento já confirmado), retornar dados do erro
    // para que o frontend possa tratar adequadamente (não cancelar pedido já pago!)
    if (error.response?.status === 409 && error.response?.data?.payment_confirmed) {
      return {
        success: false,
        payment_confirmed: true,
        pedido_id: error.response.data.pedido_id,
        valor_pago: error.response.data.valor_pago,
        msg: error.response.data.msg
      };
    }

    // Não propagar erro - cancelamento é "best effort"
    return { success: false, error: error.message };
  }
};

// ====================================================================
// FUNÇÕES DE API PARA CARRINHO (Cardápio Público)
// ====================================================================

// ✅ Buscar carrinho ativo para a sessão/cliente (público - cardápio)
export const getCarrinho = async (idEmpresa, clienteId = null) => {
  const params = clienteId ? { cliente_id: clienteId } : {};
  return apiPublic.get(`/api/v1/carrinho/${idEmpresa}`, {
    params,
    withCredentials: true // Para cookies de sessão
  });
};

// ✅ Adicionar item ao carrinho (público - cardápio)
export const addItemCarrinho = async (idEmpresa, item) => {
  return apiPublic.post(`/api/v1/carrinho/${idEmpresa}/item`, item, {
    withCredentials: true
  });
};

// ✅ Atualizar quantidade de item no carrinho (público - cardápio)
export const updateItemCarrinho = async (idEmpresa, itemId, quantity) => {
  return apiPublic.put(`/api/v1/carrinho/${idEmpresa}/item/${itemId}`, { quantity }, {
    withCredentials: true
  });
};

// ✅ Remover item do carrinho (público - cardápio)
export const removeItemCarrinho = async (idEmpresa, itemId) => {
  return apiPublic.delete(`/api/v1/carrinho/${idEmpresa}/item/${itemId}`, {
    withCredentials: true
  });
};

// ✅ Limpar todo o carrinho (público - cardápio)
export const clearCarrinho = async (idEmpresa) => {
  return apiPublic.delete(`/api/v1/carrinho/${idEmpresa}`, {
    withCredentials: true
  });
};

// ✅ Finalizar carrinho após criar pedido (público - cardápio)
export const finalizarCarrinho = async (idEmpresa, pedidoId) => {
  return apiPublic.post(`/api/v1/carrinho/${idEmpresa}/finalizar`, { pedido_id: pedidoId }, {
    withCredentials: true
  });
};

// ✅ Marcar carrinho como abandonado (público - cardápio/tracking)
export const abandonarCarrinho = async (idEmpresa, motivo = 'fechou_aba', ultimoPasso = null) => {
  return apiPublic.post(`/api/v1/carrinho/${idEmpresa}/abandonar`, {
    motivo,
    ultimo_passo: ultimoPasso
  }, {
    withCredentials: true
  });
};

// ✅ Atualizar último passo do usuário para tracking (público - cardápio)
export const atualizarPassoCarrinho = async (idEmpresa, ultimoPasso) => {
  return apiPublic.post(`/api/v1/carrinho/${idEmpresa}/atualizar-passo`, {
    ultimo_passo: ultimoPasso
  }, {
    withCredentials: true
  });
};

// ✅ Sincronizar carrinho do front (localStorage) com o backend p/ tracking de abandono.
// Usa header x-cart-session = session_token_pedeja (MESMO token do registerPedido) para
// que, ao finalizar, o backend ache o carrinho e marque finalizado/recuperado — sem falso abandono.
// Fire-and-forget no chamador: nunca deve bloquear/quebrar a UX do cardápio.
export const syncCarrinhoBackend = async (idEmpresa, itens, ultimoPasso = null, sessionToken = null) => {
  // Token vai no BODY (não em header custom) para NÃO exigir CORS preflight extra —
  // a allowlist de headers do backend não inclui x-cart-session. O endpoint /sync lê
  // body.session_token. Content-Type application/json já é permitido.
  return apiPublic.post(`/api/v1/carrinho/${idEmpresa}/sync`, {
    itens,
    ultimo_passo: ultimoPasso,
    session_token: sessionToken
  });
};

// ✅ Buscar estatísticas de abandono (admin - requer auth)
export const getEstatisticasAbandono = async (idEmpresa, dataInicio = null, dataFim = null) => {
  const params = {};
  if (dataInicio) params.dataInicio = dataInicio;
  if (dataFim) params.dataFim = dataFim;
  return api.get(`/api/v1/carrinho/${idEmpresa}/estatisticas`, { params });
};

// ✅ Listar carrinhos abandonados para remarketing (admin - requer auth)
export const getCarrinhosAbandonados = async (idEmpresa, horas = 24, limite = 50) => {
  return api.get(`/api/v1/carrinho/${idEmpresa}/abandonados`, {
    params: { horas, limite }
  });
};

// ============================================
// 📊 RELATÓRIOS AVANÇADOS V2
// ============================================

// ✅ Relatório de Vendas por Produto
export const getRelatorioVendasProduto = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/vendas-produto", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Relatório de Análise Geográfica (regiões de entrega)
export const getRelatorioGeografico = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/geografico", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Relatório de Análise de Clientes
export const getRelatorioClientesAnalise = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/clientes-analise", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Relatório de Formas de Pagamento
export const getRelatorioPagamentos = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/pagamentos", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Relatório de Horários de Pico
export const getRelatorioHorarios = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/horarios", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Relatório de Descontos Aplicados
export const getRelatorioDescontos = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/descontos", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Relatório de Estoque
export const getRelatorioEstoque = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/estoque", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Relatório de Taxas por Entregador (acerto de repasse)
export const getRelatorioEntregadores = async (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  return api.get("/api/v1/relatorios/entregadores", {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }
  });
};

// ✅ Gorjetas do periodo (para o acerto com a equipe). Le do CAIXA, nao dos
// pedidos: gorjeta nunca entra em Pedidos.valor_total porque nao e faturamento.
export const getRelatorioGorjetas = async (startDate, endDate) => {
  return api.get("/api/v1/relatorios/gorjetas", { params: { startDate, endDate } });
};

// ====================================================================
// CARDÁPIO PÚBLICO V2 — Endpoints otimizados com slug, cache e data minimization
// ====================================================================

// Mega-endpoint: categorias + itens + adicionais numa só resposta (substitui 3 chamadas)
export const getCardapioPublic = async (slugOrId, mode = 'delivery') => {
  return apiPublic.get(`/api/public/cardapio/${slugOrId}?mode=${mode}`);
};

// Status leve: aberto/fechado + tempo entrega (cache 30s)
export const getStoreStatus = async (slugOrId) => {
  return apiPublic.get(`/api/public/status/${slugOrId}`);
};

// Info completa da loja para exibição (cache 10min)
export const getStoreInfo = async (slugOrId) => {
  return apiPublic.get(`/api/public/store/${slugOrId}`);
};

// Detalhe de 1 item + seus adicionais (cache 5min)
export const getItemPublic = async (slugOrId, itemId) => {
  return apiPublic.get(`/api/public/item/${slugOrId}/${itemId}`);
};

// Cálculo de distância e frete (sem cache)
export const calcularDistanciaPublic = async (coordenadasA, coordenadasB, id_empresa) => {
  const { lat: latA, lng: lngA } = coordenadasA;
  const { lat: latB, lng: lngB } = coordenadasB;
  let url = `/api/public/distance?latA=${latA}&lngA=${lngA}&latB=${latB}&lngB=${lngB}`;
  if (id_empresa) {
    url += `&id_empresa=${id_empresa}`;
  }
  return apiPublic.get(url);
};

// Geocoding via proxy do backend.
// Frontend não pode chamar Geocoding REST direto: nossa Maps API key
// é restrita por HTTP referrer e o Google só aceita esse tipo de
// restrição em SDKs JavaScript. O backend repassa usando a chave
// server-side (restrita por API target).
//
// Uso:
//   geocodePublic({ address: 'Av Paulista 1578' })
//   geocodePublic({ latlng: '-23.5618,-46.6565' })
export const geocodePublic = async ({ address, latlng } = {}) => {
  const params = new URLSearchParams();
  if (address) params.set('address', address);
  if (latlng) params.set('latlng', latlng);
  return apiPublic.get(`/api/public/geocode?${params.toString()}`);
};

// Criar pedido (público)
export const registerPedidoPublic = async (orderData) => {
  return apiPublic.post('/api/public/order', orderData);
};

// Registrar cliente do cardápio
export const registerClientePublic = async (id_empresa, nome, telefone) => {
  return apiPublic.post('/api/public/client/register', { id_empresa, nome, telefone });
};

// Atualizar/adicionar endereço do cliente
export const updateEnderecoClientePublic = async (id_empresa, telefone, endereco, enderecoToEdit) => {
  return apiPublic.post('/api/public/client/address', {
    id_empresa,
    telefone,
    endereco,
    enderecoToEdit,
  });
};

// Remover endereço do cliente
export const deleteEnderecoClientePublic = async (id_empresa, telefone, enderecoIdToDelete) => {
  return apiPublic.delete('/api/public/client/address', {
    data: { id_empresa, telefone, enderecoIdToDelete },
  });
};

// Listar pedidos por session_token (tracking)
export const trackPedidosPublic = async (session_token) => {
  return apiPublic.get(`/api/public/orders/track/${session_token}`);
};

// Detalhe de 1 pedido por session_token
export const trackPedidoDetailPublic = async (session_token, id_pedido) => {
  return apiPublic.get(`/api/public/orders/track/${session_token}/${id_pedido}`);
};

// Verificar pedido pendente de cartão
export const checkPendingCardOrderPublic = async (idEmpresa, celularCliente) => {
  const response = await apiPublic.post('/api/public/orders/check-pending', {
    id_empresa: idEmpresa,
    celular_cliente: celularCliente,
  });
  return response.data;
};

// Cancelar pedido expirado ou com valor alterado
export const cancelExpiredOrderPublic = async (pedidoId, idEmpresa, motivo = null, force = false) => {
  try {
    const response = await apiPublic.post(`/api/public/orders/cancel-expired/${pedidoId}`, {
      id_empresa: idEmpresa,
      motivo: motivo || 'Pagamento não realizado - tempo expirado',
      force: force,
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 409 && error.response?.data?.payment_confirmed) {
      return {
        success: false,
        payment_confirmed: true,
        pedido_id: error.response.data.pedido_id,
        valor_pago: error.response.data.valor_pago,
        msg: error.response.data.msg,
      };
    }
    return { success: false, error: error.message };
  }
};

// ==============================
// RBAC: Gestao de Equipe
// ==============================
export const getTeam = async () => {
    return api.get("/api/v1/users/team");
};

export const inviteUser = async (data) => {
    return api.post("/api/v1/users/invite", data);
};

export const updateUserRole = async (userId, role) => {
    return api.put("/api/v1/users/update-role", { userId, role });
};

export const deactivateUser = async (userId) => {
    return api.put(`/api/v1/users/deactivate/${userId}`);
};

export const deletePermanentUser = async (userId) => {
    return api.delete(`/api/v1/users/delete-permanent/${userId}`);
};

// RBAC modular: Atualizar modulos de acesso de um membro
export const updateUserModules = async (userId, modules) => {
    return api.put("/api/v1/users/update-modules", { userId, modules });
};
